/*  React  */
import { Route } from 'application/Shared/Helpers';
import { TenantApp } from 'application/Shared/types/tenantApp';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { WorkatoActions, WorkatoConnection } from '../Workato/types';

interface SaasOpsApplicationContextType {
    applicationId: string | null;
    baseAdminRoute: string;
    isLicenseDataAvailable: boolean;
    isOffboardAvailable: boolean;
    isOnboardAvailable: boolean;
    isSaasOpsEnabled: boolean;
    workatoConnection?: WorkatoConnection;
}

export const SaasOpsApplicationContext =
    createContext<SaasOpsApplicationContextType>({
        applicationId: null,
        baseAdminRoute: '',
        isLicenseDataAvailable: false,
        isOffboardAvailable: false,
        isOnboardAvailable: false,
        isSaasOpsEnabled: false
    });

/**
 * This context is used to provide data for SaaS Ops for a tenant app
 */
export const SaasOpsApplicationContextProvider = ({
    tenantApp,
    children
}: {
    tenantApp: TenantApp;
    children: ReactNode;
}) => {
    const { clientId, partnerId } = useParams<{
        clientId: string;
        partnerId?: string;
    }>();

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });
    const [workatoActions, workatoProvider, applicationId] = useMemo(() => {
        if (!tenantApp?.id) {
            return [[], null, null];
        }
        return [
            tenantApp.workatoActions ?? [],
            tenantApp.application?.workatoProvider ?? null,
            tenantApp.application?.id ?? null
        ];
    }, [
        tenantApp?.id,
        tenantApp?.workatoActions,
        tenantApp?.application?.workatoProvider,
        tenantApp?.application?.id
    ]);

    const isSaasOpsEnabled = !!workatoProvider;
    const isOffboardAvailable =
        isSaasOpsEnabled && workatoActions?.includes(WorkatoActions.offboard);
    const isOnboardAvailable =
        isSaasOpsEnabled && workatoActions?.includes(WorkatoActions.onboard);
    const isLicenseDataAvailable =
        (isSaasOpsEnabled &&
            workatoActions?.includes(WorkatoActions.license)) ??
        false;

    const workatoConnection = tenantApp?.workatoConnection;

    const providerValues = useMemo(
        () => ({
            applicationId,
            baseAdminRoute,
            isLicenseDataAvailable,
            isOffboardAvailable,
            isOnboardAvailable,
            isSaasOpsEnabled,
            workatoConnection
        }),
        [
            applicationId,
            baseAdminRoute,
            isLicenseDataAvailable,
            isOffboardAvailable,
            isOnboardAvailable,
            isSaasOpsEnabled,
            workatoConnection
        ]
    );

    return (
        <SaasOpsApplicationContext.Provider value={providerValues}>
            {children}
        </SaasOpsApplicationContext.Provider>
    );
};

export const useSaasOpsApplicationContext = () => {
    const context = useContext(SaasOpsApplicationContext);

    if (context === undefined) {
        throw new Error(
            '`useSaasOpsApplicationContext` must be used within `SaasOpsApplicationContextProvider`'
        );
    }

    return context;
};

export const ConditionalSaasOpsProvider = ({
    children,
    tenantApp
}: {
    children: ReactNode;
    tenantApp: TenantApp;
}) => {
    const { applicationId } = useSaasOpsApplicationContext();

    return applicationId ? (
        children
    ) : (
        <SaasOpsApplicationContextProvider tenantApp={tenantApp}>
            {children}
        </SaasOpsApplicationContextProvider>
    );
};
